import { Component, computed, Injector, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { AbstractComponent, ConfirmationComponent, Contact, ContactService, CountryModel, DEFAULT_LOCALE, LanguageService, LOCALE_LABELS, PublicCompanyService, SignupDTO, SUPPORTED_LOCALES, SupportedLocale, UF_OPTIONS, UtilHelper } from "lib-trend-core";
import { takeUntil } from "rxjs";
import { environment } from "../../../environments/environment";

type Step = { title: string; subtitle: string };

@Component({
    selector: 'app-singup',
    templateUrl: './singup.component.html',
    styleUrls: ['./singup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignUpComponent extends AbstractComponent implements OnInit {

  stepIndex = signal(0);
  isCreatingAccount = false;
  creationComplete = false;
  creationProgress = 0;
  creationMessage = '';
  userEmail = '';
  selectedCountryCode!: string;
  selectedCompanyCountryCode!: string;
  countries: CountryModel[] = [];
  phoneMask!: string;
  companyPhoneMask!: string;
  contact: Contact;
  beeviralId: string | null;

  creationSteps = [
    { message: 'Preparando o ambiente da sua empresa...', duration: 1500 },
    { message: 'Criando sua conta de usuário...', duration: 1200 },
    { message: 'Configurando permissões...', duration: 1000 },
    { message: 'Finalizando configurações...', duration: 800 },
    { message: 'Quase lá...', duration: 500 },
  ];

  steps: Step[] = [
    { title: 'Seus Dados', subtitle: 'Informações pessoais' },
    { title: 'Empresa', subtitle: 'Dados da empresa' },
    { title: 'Localização', subtitle: 'Endereço da empresa' },
    { title: 'Resumo', subtitle: 'Conferência final' },
  ];

  segments = [
    'Material de Construção',
    'Veículos (concessionária ou revenda)',
    'E-commerce e Marketplaces',
    'Academia',
    'Educação e Treinamentos Online',
    'Cobrança',
    'Consignado',
    'Marketing Digital',
    'Agência de Marketing',
    'Varejo Físico',
    'Imobiliária',
    'Logística e Transporte',
    'Tecnologia da Informação (TI / Software / SaaS)',
    'Saúde e Bem-estar (hospitais, clínicas, estética, fitness)',
    'Alimentação e Food Service (restaurantes, delivery)',
    'Outros',
  ];

  states = UF_OPTIONS;

  locales = SUPPORTED_LOCALES;
  localeLabels = LOCALE_LABELS;

  constructor(
    injector: Injector,
    private contactService: ContactService,
    private publicCompanyService: PublicCompanyService,
    private languageService: LanguageService,
    private dialog: MatDialog,
  ) {
    super(injector);
    this.contact = {} as Contact;
  }

  onLanguageChange(event: Event): void {
    const lang = (event.target as HTMLSelectElement).value as SupportedLocale;
    this.languageService.setLanguage(lang, false);
  }

  ngOnInit(): void {
    this.setupForm();
    this.setupZipCodeListener();
    this.countries = UtilHelper.getCountries();
    this.selectedCountryCode = '+55';
    this.selectedCompanyCountryCode = this.selectedCountryCode;
    this.formGroup.patchValue({
      country: this.selectedCountryCode,
      companyCountry: this.selectedCompanyCountryCode,
    });

    this.updatePhoneMaskByCountryCode(this.selectedCountryCode);
    this.updateCompanyPhoneMaskByCountryCode(this.selectedCompanyCountryCode);
  }

  private updatePhoneMaskByCountryCode(countryCode: string): void {
    const selectedCountry = this.countries.find(country => country.code === countryCode);
    if (selectedCountry?.code === '+244') {
      this.phoneMask = '000 000 000';
    } else if (selectedCountry?.code === '+55') {
      this.phoneMask = '(00) 0000-0000||(00) 00000-0000';
    } else {
      this.phoneMask = selectedCountry?.mask || '';
    }
  }

  private updateCompanyPhoneMaskByCountryCode(countryCode: string): void {
    const selectedCountry = this.countries.find(country => country.code === countryCode);
    if (selectedCountry?.code === '+244') {
      this.companyPhoneMask = '000 000 000';
    } else if (selectedCountry?.code === '+55') {
      this.companyPhoneMask = '(00) 0000-0000||(00) 00000-0000';
    } else {
      this.companyPhoneMask = selectedCountry?.mask || '';
    }
  }

  private setupZipCodeListener(): void {
    this.formGroup.get('zipCode')?.valueChanges.subscribe((value) => {
      if (value && value.length === 8) {
        this.onZipCode(value);
      }
    });
  }

  setupForm(): void {
    const name = super.getQueryParam('name') || '';
    const email = super.getQueryParam('email') || '';
    const phone = super.getQueryParam('phone') || '';
    this.beeviralId = super.getQueryParam('bvid_c') || '';
    this.formGroup = this.formBuilder.group({

      language: [DEFAULT_LOCALE],

      // Step 1 - Personal data
      name: [name || '', [Validators.required, Validators.minLength(3)]],
      email: [email || '', [Validators.required, Validators.pattern(this.emailRegex)]],
      cpf: ['', [Validators.required, UtilHelper.cpfValidator()]],
      phone: [phone || '', [Validators.required]],
      country: [null, [Validators.required]],

      // Step 2 - Company data
      isCompany: [false],
      companyName: ['', [Validators.minLength(2)]],
      cnpj: ['', [UtilHelper.cnpjValidator()]],
      companyEmail: ['', [Validators.pattern(this.emailRegex)]],
      companyPhone: [''],
      segment: ['', [Validators.required]],
      segmentOther: [''],
      companyCountry: [null],

      // Step 3 - Location
      zipCode: ['', [Validators.required, Validators.minLength(8)]],
      state: ['', [Validators.required]],
      street: ['', [Validators.required]],
      city: ['', [Validators.required]],
      complement: [''],

      // Recaptcha
      recaptcha: ['', Validators.required],

      beeviralId: [this.beeviralId || ''],

    });

    this.updateCompanyValidators(this.formGroup.get('isCompany')?.value);
    this.formGroup.get('isCompany')?.valueChanges.subscribe((value) => {
      this.updateCompanyValidators(!!value);
    });

    this.updateSegmentValidators(this.formGroup.get('segment')?.value);
    this.formGroup.get('segment')?.valueChanges.subscribe((value) => {
      this.updateSegmentValidators(value);
    });
  }

  private updateSegmentValidators(segment: string): void {
    const segmentOther = this.formGroup.get('segmentOther');
    if (segment === 'Outros') {
      segmentOther?.enable({ emitEvent: false });
      segmentOther?.setValidators([Validators.required, Validators.minLength(2)]);
    } else {
      segmentOther?.setValidators([]);
      segmentOther?.setValue('', { emitEvent: false });
      segmentOther?.disable({ emitEvent: false });
    }
    segmentOther?.updateValueAndValidity({ emitEvent: false });
  }

  private updateCompanyValidators(isCompany: boolean): void {
    const companyName = this.formGroup.get('companyName');
    const cnpj = this.formGroup.get('cnpj');
    const companyEmail = this.formGroup.get('companyEmail');
    const companyPhone = this.formGroup.get('companyPhone');

    if (isCompany) {
      companyName?.enable({ emitEvent: false });
      cnpj?.enable({ emitEvent: false });
      companyEmail?.enable({ emitEvent: false });
      companyPhone?.enable({ emitEvent: false });

      companyName?.setValidators([Validators.required, Validators.minLength(2)]);
      companyEmail?.setValidators([Validators.required, Validators.pattern(this.emailRegex)]);
      companyPhone?.setValidators(Validators.required);
      cnpj?.setValidators([UtilHelper.cnpjValidator()]);
    } else {
      companyName?.setValidators([Validators.minLength(2)]);
      companyEmail?.setValidators([Validators.pattern(this.emailRegex)]);
      companyPhone?.setValidators(Validators.required);
      cnpj?.setValidators([UtilHelper.cnpjValidator()]);

      companyName?.disable({ emitEvent: false });
      cnpj?.disable({ emitEvent: false });
      companyEmail?.disable({ emitEvent: false });
      companyPhone?.disable({ emitEvent: false });
    }

    companyName?.updateValueAndValidity({ emitEvent: false });
    cnpj?.updateValueAndValidity({ emitEvent: false });
    companyEmail?.updateValueAndValidity({ emitEvent: false });
    companyPhone?.updateValueAndValidity({ emitEvent: false });
  }

  changeIsCompany(): void {
    const isCompany = !!this.formGroup.get('isCompany')?.value;
    this.updateCompanyValidators(isCompany);
  }

  private clampedIndex = computed(() => {
    const total = this.steps.length;
    return total ? Math.min(Math.max(this.stepIndex(), 0), total - 1) : 0;
  });

  private currentStepSignal = computed(() => this.clampedIndex() + 1);

  private progressSignal = computed(() => {
    const total = this.steps.length;
    return total > 1 ? (this.clampedIndex() / (total - 1)) * 100 : 0;
  });

  get currentStep(): number {
    return this.currentStepSignal();
  }

  get progressPct(): number {
    return this.progressSignal();
  }

  nextStep(): void {
    if (this.isCurrentStepValid()) {
      const maxIndex = Math.max(this.steps.length - 1, 0);
      this.stepIndex.update(index => Math.min(index + 1, maxIndex));
    } else {
      this.markCurrentStepFieldsAsTouched();
    }
  }

  previousStep(): void {
    this.stepIndex.update(index => Math.max(index - 1, 0));
  }

  goToStep(index: number): void {
    if (index < this.stepIndex()) {
      this.stepIndex.set(index);
    }
  }

  isCurrentStepValid(): boolean {
    if (this.stepIndex() === 1) {
      const isCompany = !!this.formGroup.get('isCompany')?.value;
      const segment = this.formGroup.get('segment')?.value;

      const fieldsToValidate = isCompany
        ? ['companyName', 'cnpj', 'companyEmail', 'companyPhone', 'segment']
        : ['segment'];

      if (segment === 'Outros') {
        fieldsToValidate.push('segmentOther');
      }

      return fieldsToValidate.every(field => this.formGroup.get(field)?.valid);
    }

    const stepFields: { [key: number]: string[] } = {
      0: ['name', 'email', 'cpf', 'country', 'phone'],
      1: ['isCompany', 'companyName', 'cnpj', 'companyEmail', 'companyPhone', 'segment', 'segmentOther', 'companyCountry'],
      2: ['zipCode', 'state', 'street', 'city'],
      3: [],
    };

    const fields = stepFields[this.stepIndex()];
    return fields.every(field => this.formGroup.get(field)?.valid);
  }

  markCurrentStepFieldsAsTouched(): void {
    if (this.stepIndex() === 1) {
      const isCompany = !!this.formGroup.get('isCompany')?.value;
      const segment = this.formGroup.get('segment')?.value;

      const fieldsToTouch = isCompany
        ? ['companyName', 'cnpj', 'companyEmail', 'companyPhone', 'segment']
        : ['segment'];

      if (segment === 'Outros') {
        fieldsToTouch.push('segmentOther');
      }

      fieldsToTouch.forEach(field => this.formGroup.get(field)?.markAsTouched());
      return;
    }

    const stepFields: { [key: number]: string[] } = {
      0: ['name', 'email', 'cpf', 'phone', 'country'],
      1: ['isCompany', 'companyName', 'cnpj', 'companyEmail', 'companyPhone', 'segment', 'segmentOther', 'companyCountry'],
      2: ['zipCode', 'state', 'street', 'city'],
      3: [],
    };

    const fields = stepFields[this.stepIndex()];
    fields.forEach(field => this.formGroup.get(field)?.markAsTouched());
  }

  onZipCode(cep: string): void {
    if (cep?.length === 8) {
      this.getZipCode(cep);
    }
  }

  private getZipCode(cep: string): void {
    this.contactService.searchCep(cep)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.formGroup.patchValue({
            street: res.street,
            city: res.city,
            state: res.state
          });
        },
        error: () => {
          this.alertService.error(this.t('signup.home.zipCodeSearchError'));
        }
      });
  }

  openConfirmCreate(): void {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('signup.home.confirmCreateCompany'),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.onSubmit();
      }
    });
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      const isCompany = !!this.formGroup.get('isCompany')?.value;
      const payload = {
        language: this.formGroup.get('language')?.value,
        name: this.formGroup.get('name')?.value,
        email: this.formGroup.get('email')?.value,
        cpf: this.formGroup.get('cpf')?.value?.replace(/\D/g, ''),
        phone: (this.formGroup.get('country')?.value || '') + (this.formGroup.get('phone')?.value?.replace(/\D/g, '') || ''),
        companyName: isCompany ? this.formGroup.get('companyName')?.value : this.formGroup.get('name')?.value,
        cnpj: isCompany ? this.formGroup.get('cnpj')?.value?.replace(/\D/g, '') : this.formGroup.get('cpf')?.value?.replace(/\D/g, ''),
        companyEmail: isCompany ? this.formGroup.get('companyEmail')?.value : this.formGroup.get('email')?.value,
        companyPhone: isCompany ? (this.formGroup.get('companyCountry')?.value || '') + (this.formGroup.get('companyPhone')?.value?.replace(/\D/g, '') || '') : (this.formGroup.get('country')?.value || '') + (this.formGroup.get('phone')?.value?.replace(/\D/g, '') || ''),
        zipCode: this.formGroup.get('zipCode')?.value?.replace(/\D/g, ''),
        state: this.formGroup.get('state')?.value,
        street: this.formGroup.get('street')?.value,
        city: this.formGroup.get('city')?.value,
        complement: this.formGroup.get('complement').value,
        segment: this.formGroup.get('segment')?.value,
        segmentOther: this.formGroup.get('segment')?.value === 'Outros' ? this.formGroup.get('segmentOther')?.value : null,
        recaptchaToken: this.formGroup.get('recaptcha')?.value,
        beeviralId: this.formGroup.get('beeviralId')?.value,
        beeviralCampaignToken: environment.beeviralCampaignToken,
        trial: {
          activated: true,
          startDate: new Date(),
          durationDays: 2
        }
      };

      this.userEmail = this.formGroup.get('email')?.value;

      this.startCreationProgress();

      this.publicCompanyService.signup(payload as SignupDTO).subscribe({
        next: (response) => {
          if (!!response.company && !!response.user) {
            this.completeCreation();
          }
        },
        error: (err) => {
          this.failCreation(err.error?.message || this.t('signup.home.createAccountError'));
        }
      });
    } else {
      this.validateAllFormFields(this.formGroup);
    }
  }

  private startCreationProgress(): void {
    this.isCreatingAccount = true;
    this.creationProgress = 0;
    this.creationMessage = this.creationSteps[0].message;

    let currentStep = 0;
    const totalSteps = this.creationSteps.length;
    const maxProgress = 85;
    const progressPerStep = maxProgress / totalSteps;

    const processStep = () => {
      if (currentStep < totalSteps) {
        this.creationMessage = this.creationSteps[currentStep].message;

        const targetProgress = Math.min((currentStep + 1) * progressPerStep, maxProgress);
        this.animateProgress(this.creationProgress, targetProgress, 300);

        setTimeout(() => {
          currentStep++;
          processStep();
        }, this.creationSteps[currentStep].duration);
      } else {
        this.creationMessage = this.t('signup.home.awaitingServerConfirmation');
      }
    };

    processStep();
  }

  completeCreation(): void {
    this.animateProgress(this.creationProgress, 100, 400);
    this.creationMessage = this.t('signup.home.accountCreatedSuccess');

    setTimeout(() => {
      this.isCreatingAccount = false;
      this.creationComplete = true;
    }, 1000);
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  failCreation(errorMessage: string): void {
    this.isCreatingAccount = false;
    this.creationProgress = 0;
    this.alertService.error(errorMessage);
  }

  private animateProgress(from: number, to: number, duration: number): void {
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      this.creationProgress = from + (to - from) * progress;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  onCountryChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedCountryCode = selectElement.value;

    this.formGroup.patchValue({
      country: this.selectedCountryCode,
    });

    this.updatePhoneMaskByCountryCode(this.selectedCountryCode);
  }

  onCompanyCountryChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedCompanyCountryCode = selectElement.value;

    this.formGroup.patchValue({
      companyCountry: this.selectedCompanyCountryCode,
    });

    this.updateCompanyPhoneMaskByCountryCode(this.selectedCompanyCountryCode);
    this.formGroup.get('companyPhone')?.updateValueAndValidity({ emitEvent: false });
  }

  keyPressOnlyNumbers(event: any) {
    const pattern = /[0-9\+\-\ ]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode != 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  public get recaptchaToken(): string {
    return String(this.environment['recaptchaToken']);
  }
}
